import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ArAsset, ArLandscapeRow } from '../../types';
import ArFilterBar, { ArFilters, emptyFilters, ArChipOption } from './FilterBar';
import { TIER_NAME, assetMod, PH_COL, phBucket } from './arHelpers';
import type { ArSharedProps } from './AssetRadarPage';

const SECTION_ORDER: ArLandscapeRow['section'][] = ['design', 'efficacy', 'safety', 'sources'];
const SEC_NAME: Record<string, string> = { design: 'Design', efficacy: 'Efficacy', safety: 'Safety', sources: 'Sources' };

function cellText(text: string | undefined) {
  const t = (text || '').trim();
  if (!t) return '—';
  return t;
}

function hasReportedCell(v: string | undefined) {
  const t = (v || '').trim();
  return !!t && !/^NR$|^N\/A$/i.test(t);
}

/** Does this asset have at least one reported efficacy result? Same heuristic keyMetrics() already uses
 * per-cell, applied across every efficacy row to decide whether the whole column has clinical data. */
function hasClinicalData(land: { rows: ArLandscapeRow[] } | null, a: ArAsset) {
  if (!land) return false;
  return land.rows.some((r) => r.section === 'efficacy' && hasReportedCell(r.cells[a.idx]));
}

function nctLinks(text: string) {
  const t = cellText(text);
  const parts: (string | React.ReactElement)[] = [];
  let last = 0; const re = /NCT\d{8}/g; let m: RegExpExecArray | null;
  while ((m = re.exec(t))) {
    if (m.index > last) parts.push(t.slice(last, m.index));
    parts.push(<a key={m.index} href={`https://clinicaltrials.gov/study/${m[0]}`} target="_blank" rel="noopener noreferrer">{m[0]}</a>);
    last = m.index + m[0].length;
  }
  if (last < t.length) parts.push(t.slice(last));
  return parts.length ? parts : t;
}

const LandscapeTab: React.FC<ArSharedProps> = ({ ind, tracker }) => {
  const land = ind?.land || null;
  const [mode, setMode] = useState<'table' | 'cards'>('table');
  const [filters, setFilters] = useState<ArFilters>(emptyFilters());
  const [compareSet, setCompareSet] = useState<Set<number>>(new Set());
  const [comparing, setComparing] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({ sources: true });
  const [openCells, setOpenCells] = useState<Set<string>>(new Set());
  const [fullCells, setFullCells] = useState(false);
  // This table is meant to show just columns for assets with reported clinical data (a non-empty,
  // non-"NR"/"N/A" efficacy cell) — on by default, with an escape hatch to see every column.
  const [clinicalOnly, setClinicalOnly] = useState(true);

  // "Show in table" / Overview jump (asset_radar/overview.js's `S.flash`): the Overview tab can send an
  // `?asset=` query param naming the program it was asked to open. Reset any filters that might hide it,
  // make sure "Clinical data only" doesn't hide it either, then scroll its column into view and flash it
  // (radar.css's th/td.flash animation, already in the stylesheet but unused until now).
  const [params] = useSearchParams();
  const assetParam = params.get('asset') || '';
  const flashedFor = useRef('');

  const tierOptions: ArChipOption[] = useMemo(() => {
    if (!land) return [];
    const present = new Set(land.assets.map((a) => a.tier));
    return [1, 2, 3].filter((t) => present.has(t as 1 | 2 | 3)).map((t) => ({ value: String(t), label: TIER_NAME[t], color: `var(--t${t})` }));
  }, [land]);

  const modOptions: ArChipOption[] = useMemo(() => {
    if (!land) return [];
    const counts = new Map<string, number>();
    land.assets.forEach((a) => { const m = assetMod(tracker, a); counts.set(m, (counts.get(m) || 0) + 1); });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([v, c]) => ({ value: v, label: v, count: c }));
  }, [land, tracker]);

  const phaseOptions: ArChipOption[] = useMemo(() => {
    if (!land) return [];
    const ord = (p: string) => (p === 'Approved' ? 0 : p === 'Other' ? 99 : 10 - parseFloat(p.replace(/[^0-9.\/]/g, '').split('/').pop() || '0'));
    const vals = [...new Set(land.assets.map((a) => a.phase))].sort((a, b) => ord(a) - ord(b));
    return vals.map((p) => ({ value: p, label: p, color: PH_COL[phBucket(p)] }));
  }, [land]);

  const hasLeads = !!land?.assets.some((a) => a.lead);
  // If this landscape has no efficacy rows at all, there's nothing to filter on -- never hide every asset.
  const hasAnyClinicalData = !!land?.rows.some((r) => r.section === 'efficacy');

  const targetAsset = useMemo(() => {
    if (!assetParam || !land) return null;
    const norm = (s: string) => s.trim().toLowerCase();
    return land.assets.find((a) => norm(a.name) === norm(assetParam)) || null;
  }, [assetParam, land]);
  const resetAttempted = useRef('');

  useEffect(() => {
    if (!targetAsset || flashedFor.current === assetParam) return;
    const cells = document.querySelectorAll(`[data-a="${targetAsset.idx}"]`);
    if (!cells.length) {
      // Not on screen yet -- a filter, "Clinical data only", or a leftover compare selection may be
      // hiding it. Clear those (once) and this effect retries after the re-render they cause.
      if (resetAttempted.current !== assetParam) {
        resetAttempted.current = assetParam;
        setFilters(emptyFilters());
        setComparing(false);
        if (clinicalOnly && hasAnyClinicalData && !hasClinicalData(land, targetAsset)) setClinicalOnly(false);
      }
      return;
    }
    flashedFor.current = assetParam;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    cells[0].scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduced ? 'auto' : 'smooth' });
    cells.forEach((el) => {
      el.classList.remove('flash');
      void (el as HTMLElement).offsetWidth;
      el.classList.add('flash');
    });
  });

  const passAsset = (a: ArAsset) => {
    if (clinicalOnly && hasAnyClinicalData && !hasClinicalData(land, a)) return false;
    if (filters.tiers.size && !filters.tiers.has(String(a.tier))) return false;
    if (filters.mods.size && !filters.mods.has(assetMod(tracker, a))) return false;
    if (filters.phases.size && !filters.phases.has(a.phase)) return false;
    if (filters.leads && !a.lead) return false;
    if (filters.q) {
      const t = `${a.name} ${a.moa} ${a.sponsor}`.toLowerCase();
      if (!t.includes(filters.q.toLowerCase())) return false;
    }
    return true;
  };

  if (!land) {
    return (
      <div className="state">
        <h2>No landscape tab for {ind?.label}</h2>
        <p>Trials for this setting are on the Timeline view.</p>
      </div>
    );
  }

  let assets = land.assets.filter(passAsset);
  if (comparing) assets = land.assets.filter((a) => compareSet.has(a.idx));

  return (
    <>
      <ArFilterBar tierOptions={tierOptions} modOptions={modOptions} phaseOptions={phaseOptions} showLeads hasLeads={hasLeads} filters={filters} onChange={setFilters} />

      <div className="tblbar">
        <div className="seg" role="group" aria-label="Layout">
          {(['table', 'cards'] as const).map((k) => (
            <button key={k} type="button" aria-pressed={mode === k} onClick={() => { setMode(k); setComparing(false); }} style={{ textTransform: 'capitalize' }}>{k}</button>
          ))}
        </div>
        {hasAnyClinicalData && (
          <div className="seg" role="group" aria-label="Scope">
            {([[true, 'Clinical data only'], [false, 'All assets']] as const).map(([v, l]) => (
              <button key={String(v)} type="button" aria-pressed={clinicalOnly === v} onClick={() => setClinicalOnly(v)}>{l}</button>
            ))}
          </div>
        )}
        {mode === 'table' && (
          <>
            {comparing ? (
              <>
                <button type="button" className="btn" onClick={() => setComparing(false)}>Back to cards</button>
                <strong>Comparing {assets.length} assets</strong>
              </>
            ) : (
              <span className="meta">{assets.length} of {land.assets.length} assets</span>
            )}
            <div className="spacer" />
            <button type="button" className="btn ghost" aria-pressed={fullCells} onClick={() => setFullCells((v) => !v)}>
              {fullCells ? 'Clip long cells' : 'Show full cells'}
            </button>
          </>
        )}
      </div>

      {!assets.length ? (
        <div className="state">
          <h2>No assets match these filters</h2>
          <button type="button" className="btn" onClick={() => setFilters(emptyFilters())}>Clear filters</button>
        </div>
      ) : mode === 'cards' && !comparing ? (
        <CardsView land={land} assets={assets} compareSet={compareSet} setCompareSet={setCompareSet} onCompareNow={() => setComparing(true)} onShowInTable={() => setMode('table')} />
      ) : (
        <TableView
          land={land} assets={assets} collapsed={collapsed} setCollapsed={setCollapsed}
          openCells={openCells} setOpenCells={setOpenCells} fullCells={fullCells}
        />
      )}
    </>
  );
};

const TableView: React.FC<{
  land: NonNullable<ArSharedProps['ind']>['land'];
  assets: ArAsset[];
  collapsed: Record<string, boolean>; setCollapsed: (f: (c: Record<string, boolean>) => Record<string, boolean>) => void;
  openCells: Set<string>; setOpenCells: (f: (s: Set<string>) => Set<string>) => void;
  fullCells: boolean;
}> = ({ land, assets, collapsed, setCollapsed, openCells, setOpenCells, fullCells }) => {
  if (!land) return null;
  // Tier band header: consecutive assets of one tier share a header cell.
  const bands: { tier: number; span: number }[] = [];
  for (let i = 0; i < assets.length;) {
    let j = i; while (j < assets.length && assets[j].tier === assets[i].tier) j++;
    bands.push({ tier: assets[i].tier, span: j - i }); i = j;
  }
  return (
    <div className={`tablewrap${fullCells ? ' fullcells' : ''}`}>
      <table className="land">
        <thead>
          <tr className="band">
            <th className="corner" scope="col">SOC vs emerging</th>
            {bands.map((b, i) => (
              <th key={i} colSpan={b.span} className={`t${b.tier}`} scope="colgroup">{TIER_NAME[b.tier]}</th>
            ))}
          </tr>
          <tr className="names">
            <th className="corner" scope="col">Asset</th>
            {assets.map((a) => (
              <th key={a.idx} scope="col" className={a.lead ? 'lead' : undefined} data-a={a.idx} style={{ '--tc': `var(--t${a.tier})` } as React.CSSProperties}>
                <div className="aname">{a.name}</div>
                <div className="atags">
                  {a.lead && <span className="tag lead">Lead</span>}
                  <span className="tag ph">{a.phase}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SECTION_ORDER.map((sec) => {
            const rows = land.rows.filter((r) => r.section === sec);
            if (!rows.length) return null;
            const isCollapsed = collapsed[sec] ?? sec === 'sources';
            const what = sec === 'sources' ? 'sources' : sec;
            return (
              <React.Fragment key={sec}>
                <tr className="div" data-sec={sec}>
                  <th colSpan={assets.length + 1} scope="colgroup">
                    <span className="dl">
                      {SEC_NAME[sec]}
                      <button
                        type="button" className="secbtn" aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? `Show ${what}` : `Hide ${what}`}
                        title={isCollapsed ? `Show ${what} (${rows.length} row${rows.length === 1 ? '' : 's'})` : `Hide ${what}`}
                        onClick={() => setCollapsed((c) => ({ ...c, [sec]: !isCollapsed }))}
                      >
                        {isCollapsed ? '▸' : '▾'}
                      </button>
                      {isCollapsed && <span className="seccount">{rows.length} hidden</span>}
                    </span>
                  </th>
                </tr>
                {!isCollapsed && rows.map((r, ri) => (
                  <tr key={ri}>
                    <th className="rl" scope="row">{r.label}</th>
                    {assets.map((a) => {
                      const key = `${sec}|${r.label}|${a.idx}`;
                      const open = openCells.has(key);
                      const text = cellText(r.cells[a.idx]);
                      return (
                        <td
                          key={a.idx} data-a={a.idx}
                          onClick={() => setOpenCells((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; })}
                          className={`cell${sec === 'efficacy' ? ' mono' : ''}${a.lead ? ' lead' : ''}${open ? ' open' : ''}`}
                        >
                          <div className="c">{nctLinks(text)}</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

function keyMetrics(land: NonNullable<ArSharedProps['ind']>['land'], a: ArAsset): ArLandscapeRow[] {
  if (!land) return [];
  const has = (r: ArLandscapeRow) => { const v = (r.cells[a.idx] || '').trim(); return v && !/^NR$|^N\/A$/i.test(v); };
  const eff = land.rows.filter((r) => r.section === 'efficacy' && has(r)).slice(0, 3);
  const saf = land.rows.filter((r) => r.section === 'safety' && /overall grade|grade\s*(?:≥|>=|≧)\s*3|g\s*(?:≥|>=)\s*3/i.test(r.label)).slice(0, 1);
  return [...eff, ...saf];
}

const CardsView: React.FC<{
  land: NonNullable<ArSharedProps['ind']>['land']; assets: ArAsset[];
  compareSet: Set<number>; setCompareSet: (f: (s: Set<number>) => Set<number>) => void;
  onCompareNow: () => void; onShowInTable: () => void;
}> = ({ land, assets, compareSet, setCompareSet, onCompareNow, onShowInTable }) => {
  if (!land) return null;
  return (
    <>
      <div className="tblbar"><span className="meta">{assets.length} of {land.assets.length} assets. Select up to 4 to compare side by side.</span></div>
      <div className="cards">
        {assets.map((a) => {
          const sel = compareSet.has(a.idx);
          const kv = keyMetrics(land, a);
          return (
            <article key={a.idx} className={`card${a.lead ? ' lead' : ''}`} style={{ '--tc': `var(--t${a.tier})` } as React.CSSProperties} data-a={a.idx}>
              <div>
                <span className="cap" style={{ color: `var(--t${a.tier})` }}>{TIER_NAME[a.tier]}</span>
                <h3>{a.name}</h3>
                <div className="spons">{a.sponsor || 'Sponsor NR'}</div>
              </div>
              <div className="atags">
                {a.lead && <span className="tag lead">Lead</span>}
                <span className="tag ph">{a.phase}</span>
              </div>
              {a.moa && <div className="moa">{a.moa}</div>}
              <dl className="kv">
                {kv.length ? kv.map((r, i) => (
                  <React.Fragment key={i}>
                    <dt>{r.label.replace(/\s*\(.*?\)\s*$/, '')}</dt>
                    <dd>{r.cells[a.idx] || 'NR'}</dd>
                  </React.Fragment>
                )) : <><dt>Data</dt><dd>NR</dd></>}
              </dl>
              <footer>
                <button
                  type="button" aria-pressed={sel} disabled={!sel && compareSet.size >= 4}
                  className={`btn${sel ? ' sel' : ''}`}
                  onClick={() => setCompareSet((s) => { const n = new Set(s); n.has(a.idx) ? n.delete(a.idx) : n.add(a.idx); return n; })}
                >
                  {sel ? 'Selected' : 'Compare'}
                </button>
                <button type="button" className="btn ghost" onClick={onShowInTable}>Show in table</button>
              </footer>
            </article>
          );
        })}
      </div>
      {compareSet.size > 0 && (
        <div className="tray">
          <strong>Compare</strong>
          {[...compareSet].map((i) => {
            const a = land.assets[i];
            if (!a) return null;
            return (
              <span key={i} className="tc">
                {a.name}
                <button type="button" aria-label={`Remove ${a.name}`} onClick={() => setCompareSet((s) => { const n = new Set(s); n.delete(i); return n; })}>{'✕'}</button>
              </span>
            );
          })}
          <div className="spacer" />
          <button type="button" className="btn dark" disabled={compareSet.size < 2} onClick={onCompareNow}>
            Compare {compareSet.size} side by side
          </button>
        </div>
      )}
    </>
  );
};

export default LandscapeTab;
